import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { Btn, Badge, Modal, Input, Loading, EmptyState, Toggle, SectionLabel } from '../components/ui';

const SCREEN_GROUPS = [
  {
    group: 'Masters',
    items: [
      { key: 'masters_home', label: 'Masters home' },
      { key: 'masters_divisions', label: 'Division master' },
      { key: 'masters_factories', label: 'Factory master' },
      { key: 'masters_shiftlines', label: 'Shift-line master' },
      { key: 'masters_operations', label: 'Operation master' },
      { key: 'masters_fundgroups', label: 'Fund. operation master' },
      { key: 'masters_machines', label: 'Machine master' },
      { key: 'masters_employees', label: 'Employee master' },
      { key: 'masters_downtime_reasons', label: 'Downtime reason master' },
    ]
  },
  {
    group: 'Planning',
    items: [
      { key: 'ob', label: 'Operation bulletin' },
      { key: 'line_design', label: 'Line design' },
      { key: 'monthly_summary', label: 'Monthly summary' },
    ]
  },
  {
    group: 'Operations',
    items: [
      { key: 'deployment', label: 'Deployment' },
      { key: 'daily_balancing', label: 'Daily balancing' },
      { key: 'hourly_reporting', label: 'Hourly reporting' },
    ]
  },
  {
    group: 'Reports',
    items: [
      { key: 'skill_matrix', label: 'Skill matrix' },
      { key: 'skill_journey', label: 'Skill journey' },
    ]
  },
  {
    group: 'Dashboards',
    items: [
      { key: 'dashboard_supervisor', label: 'Supervisor dashboard' },
      { key: 'dashboard_plant', label: 'Plant head dashboard' },
      { key: 'dashboard_downtime', label: 'Downtime dashboard' },
      { key: 'plant_optimiser', label: 'Plant optimiser' },
    ]
  },
  // NOTE: "User access" and "Role master" are intentionally NOT listed here.
  // Access to those two screens is hardcoded to true super admins only —
  // it can never be granted through a role's screen permissions.
];

export default function RoleMaster() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState(null); // null = landing view
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [form, setForm] = useState({ name: '' });
  const [error, setError] = useState('');
  const [perms, setPerms] = useState({}); // { screen_key: bool }
  const [permsLoading, setPermsLoading] = useState(false);
  const [permsSaved, setPermsSaved] = useState(false);

  useEffect(() => { document.getElementById('lc-page-title').textContent = 'Role master'; }, []);

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/roles');
      setRoles(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  const fetchPerms = useCallback(async (roleId) => {
    setPermsLoading(true);
    try {
      const { data } = await api.get(`/roles/${roleId}/permissions`);
      const map = {};
      data.forEach(p => { map[p.screen_key] = p.can_access; });
      setPerms(map);
    } catch (e) { console.error(e); }
    setPermsLoading(false);
  }, []);

  useEffect(() => { fetchRoles(); }, [fetchRoles]);
  useEffect(() => { if (selectedRole) fetchPerms(selectedRole.id); }, [selectedRole, fetchPerms]);

  const openAdd = () => {
    setEditingRole(null);
    setForm({ name: '' });
    setError(''); setModalOpen(true);
  };

  const openEdit = (r) => {
    setEditingRole(r);
    setForm({ name: r.name });
    setError(''); setModalOpen(true);
  };

  const handleSaveRole = async () => {
    if (!form.name.trim()) { setError('Role name is required'); return; }
    try {
      if (editingRole) {
        await api.put(`/roles/${editingRole.id}`, form);
      } else {
        await api.post('/roles', form);
      }
      setModalOpen(false); fetchRoles();
    } catch (e) { setError(e.response?.data?.error || 'Error saving'); }
  };

  const handleDeleteRole = async (r) => {
    if (!window.confirm(`Delete role "${r.name}"? Users with this role keep their current access but lose the role link.`)) return;
    try {
      await api.delete(`/roles/${r.id}`);
      fetchRoles();
    } catch (e) { alert(e.response?.data?.error || 'Error deleting'); }
  };

  const togglePerm = (key) => {
    setPerms(p => ({ ...p, [key]: !p[key] }));
    setPermsSaved(false);
  };

  const handleSavePerms = async () => {
    const permissions = Object.keys(perms).map(screen_key => ({ screen_key, can_access: !!perms[screen_key] }));
    try {
      await api.put(`/roles/${selectedRole.id}/permissions`, { permissions });
      setPermsSaved(true);
    } catch (e) { alert(e.response?.data?.error || 'Error saving permissions'); }
  };

  // ── Landing: role list ────────────────────────────────────────────────────
  if (!selectedRole) {
    return (
      <div style={{ padding: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 500 }}>
              Role master <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-secondary)', marginLeft: 6 }}>{roles.length} roles</span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Create roles and assign which screens they can access</div>
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <Btn variant="primary" size="sm" onClick={openAdd}>+ Add role</Btn>
          </div>
        </div>

        {loading ? <Loading /> : roles.length === 0 ? (
          <EmptyState message="No roles created yet" action={<Btn variant="primary" onClick={openAdd}>+ Add role</Btn>} />
        ) : (
          <div style={{ border: '0.5px solid var(--border-md)', borderRadius: 10, overflow: 'hidden', background: 'var(--bg-primary)' }}>
            <table className="lc-table">
              <thead>
                <tr><th>#</th><th>Role name</th><th>Type</th><th></th></tr>
              </thead>
              <tbody>
                {roles.map((r, i) => (
                  <tr key={r.id}>
                    <td style={{ color: 'var(--text-tertiary)', fontSize: 11.5 }}>{i + 1}</td>
                    <td style={{ fontWeight: 500, cursor: 'pointer' }} onClick={() => setSelectedRole(r)}>{r.name}</td>
                    <td>{r.is_super_admin ? <Badge type="blue">Super admin</Badge> : <Badge type="gray">Standard</Badge>}</td>
                    <td style={{ display: 'flex', gap: 10 }}>
                      <a style={{ fontSize: 11, color: 'var(--acc)', cursor: 'pointer' }} onClick={() => setSelectedRole(r)}>Screens →</a>
                      <a style={{ fontSize: 11, color: 'var(--acc)', cursor: 'pointer' }} onClick={() => openEdit(r)}>Edit</a>
                      <a style={{ fontSize: 11, color: 'var(--red)', cursor: 'pointer' }} onClick={() => handleDeleteRole(r)}>Delete</a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editingRole ? 'Edit role' : 'Add role'} width={360}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Role name</div>
              <Input value={form.name} onChange={v => setForm(p => ({ ...p, name: v }))} placeholder="e.g. Line Leader" style={{ width: '100%' }} />
            </div>
            {error && <div style={{ fontSize: 11.5, color: 'var(--red)', background: 'var(--red-bg)', padding: '6px 9px', borderRadius: 6 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
              <Btn onClick={() => setModalOpen(false)}>Cancel</Btn>
              <Btn variant="primary" onClick={handleSaveRole}>Save</Btn>
            </div>
          </div>
        </Modal>
      </div>
    );
  }

  // ── Detail: screen permissions for selected role ─────────────────────────
  return (
    <div style={{ padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <a onClick={() => { setSelectedRole(null); setPermsSaved(false); }}
          style={{ fontSize: 12, color: 'var(--acc)', cursor: 'pointer' }}>
          ← All roles
        </a>
        <span style={{ color: 'var(--border-md)' }}>/</span>
        <div>
          <span style={{ fontSize: 15, fontWeight: 500 }}>{selectedRole.name}</span>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)', marginLeft: 8 }}>Screen access</span>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          {permsSaved && <span style={{ fontSize: 11, color: 'var(--grn)' }}>Saved ✓</span>}
          <Btn variant="primary" size="sm" onClick={handleSavePerms}>Save permissions</Btn>
        </div>
      </div>

      {selectedRole.is_super_admin && (
        <div style={{ fontSize: 11.5, color: 'var(--acc)', background: 'var(--acc-bg)', padding: '8px 12px', borderRadius: 8, marginBottom: 14 }}>
          This role is marked Super admin — it typically should have all screens enabled.
        </div>
      )}

      {permsLoading ? <Loading /> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 14 }}>
          {SCREEN_GROUPS.map(section => (
            <div key={section.group} style={{ border: '0.5px solid var(--border-md)', borderRadius: 10, padding: 14, background: 'var(--bg-primary)' }}>
              <SectionLabel>{section.group}</SectionLabel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {section.items.map(item => (
                  <div key={item.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ fontSize: 12.5 }}>{item.label}</div>
                    <Toggle value={!!perms[item.key]} onChange={() => togglePerm(item.key)} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}